import { useState, useEffect } from "react";
import { todayYmd, receiptDateToYmd } from "@shared/dates";
import { ExpenseHistory, ExpenseStory, EditedTag } from "@/components/History";
import { EditExpenseSheet } from "@/components/EditExpenseSheet";
import { AddExpenseSheet } from "@/components/AddExpenseSheet";
import { hasFullAccess } from "@shared/access";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, apiFormRequest, queryClient } from "@/lib/queryClient";
import type { Group, Expense, SafeUser, ActivityLog } from "@shared/schema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, ArrowLeft, Trash2, Shuffle, Receipt, UserPlus, X, HandCoins, CheckCircle2, AlertTriangle, Camera, Mail, Loader2, Crown, Shield, LogOut, UserMinus, Clock, Check, Ghost, FileText, Pencil, MoreVertical, Upload, Download, Repeat, ChevronDown, ChevronRight, Copy, MessageCircle, Share2, Users2 } from "lucide-react";
import { shareInviteLink, shareAppLink } from "@/lib/share";
import { Switch } from "@/components/ui/switch";
import { isInTWA } from "@/lib/platform";
import { ScanReceiptButton } from "@/components/ScanReceiptButton";
import { CurrencySelector, formatMoney } from "@/components/CurrencySelector";
import { ExpenseAmount } from "@/components/ExpenseAmount";
import { FitText } from "@/components/FitText";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { calculateGroupBalances, simplifyDebts, calculatePairwiseBalances } from "@/lib/simplify";
import { isEffectivelySettled, displayBalance, AMOUNT_IN_CLASS, AMOUNT_OUT_CLASS } from "@/lib/balance-display";
import { PaymentMethodsView } from "@/components/PaymentMethods";
import { ShareChannels } from "@/components/ShareChannels";
import { track } from "@/lib/analytics";
import { maybeAskForReview } from "@/lib/native-review";
import { inviteUrl } from "@/lib/invite-handoff";

export default function GroupDetail({ groupId }: { groupId: string }) {
  const { user } = useAuth();
  const userCurrency = user?.defaultCurrency;
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [membersSheetOpen, setMembersSheetOpen] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  // Shareable invite link (V1 — open join, one active link per group)
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  // Instant-add by email (existing Spliiit users) — lives inside the share-link dialog
  const [quickAddEmail, setQuickAddEmail] = useState("");
  // simplifyDebts is now a persistent group setting, read from group?.simplifyDebts
  const [editExpense, setEditExpense] = useState<Expense | null>(null);
  const [addDraft, setAddDraft] = useState<{ description?: string; amount?: number } | null>(null);

  // Bridge from Personal Finance ("Split this" → this group): if a draft was
  // stashed, pre-fill the Add Expense form and open it. One-shot — cleared on read.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("spliiit_split_draft");
      if (!raw) return;
      sessionStorage.removeItem("spliiit_split_draft");
      const draft = JSON.parse(raw);
      if (draft && typeof draft.amount === "number") {
        setAddDraft({ description: typeof draft.description === "string" ? draft.description : "", amount: draft.amount });
        setAddOpen(true);
      }
    } catch {}
  }, []);
  // Member-balances accordion — collapsed by default. The user's own
  // balance is always visible above ("You owe X"); the everyone-else
  // breakdown is opt-in to keep the group view tidy on large groups
  // (e.g., 10-person trips where the list would otherwise dominate).
  const [memberBalancesOpen, setMemberBalancesOpen] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [settleUpOpen, setSettleUpOpen] = useState(false);
  const [settlePayerId, setSettlePayerId] = useState("");
  const [settleReceiverId, setSettleReceiverId] = useState("");
  const [settleAmount, setSettleAmount] = useState("");

  // Delete expense confirmation
  const [deleteExpenseId, setDeleteExpenseId] = useState<string | null>(null);

  // Expense detail dialog
  const [detailExpense, setDetailExpense] = useState<any>(null);

  // Receipt detail dialog
  const [receiptExpenseId, setReceiptExpenseId] = useState<string | null>(null);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);


  // Delete group: 2-step confirmation
  const [deleteGroupStep, setDeleteGroupStep] = useState<0 | 1 | 2>(0);

  // Group rename
  const [renaming, setRenaming] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // Member action dialog state
  const [memberActionMember, setMemberActionMember] = useState<SafeUser | null>(null);
  // Tapping a member you can't admin-act on → show their payment info instead.
  const [memberInfoMember, setMemberInfoMember] = useState<SafeUser | null>(null);

  // Ghost invite dialog state
  const [ghostInviteMember, setGhostInviteMember] = useState<SafeUser | null>(null);
  const [ghostInviteEmail, setGhostInviteEmail] = useState("");
  const [ghostInviting, setGhostInviting] = useState(false);

  // Import into group
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStep, setImportStep] = useState<"upload" | "map" | "preview">("upload");
  const [importCsvNames, setImportCsvNames] = useState<string[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({}); // csvName → userId or "new:email"
  const [importNewEmails, setImportNewEmails] = useState<Record<string, string>>({}); // csvName → email for new members
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importImporterName, setImportImporterName] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const [, setLocation] = useLocation();

  const { data: group } = useQuery<Group>({ queryKey: ["/api/groups", groupId] });
  const { data: members = [] } = useQuery<SafeUser[]>({
    queryKey: ["/api/groups", groupId, "members"],
    enabled: !!group,
  });
  const { data: expenses = [] } = useQuery<Expense[]>({
    queryKey: ["/api/expenses/group", groupId],
  });
  const { data: groupActivity = [] } = useQuery<ActivityLog[]>({
    queryKey: ["/api/groups", groupId, "activity"],
    enabled: !!group,
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "history"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activity"] });
      setDeleteExpenseId(null);
      toast({ title: "Expense removed" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
      setDeleteExpenseId(null);
    },
  });

  // Can the current user delete this expense?
  const canDeleteExpense = (expense: Expense) => {
    return expense.addedById === user?.id || user?.isAdmin;
  };

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/invite`, {
        email: inviteEmail.trim().toLowerCase(),
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "invites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setInviteEmail("");
      setInviteOpen(false);
      const isAdminSender = data.adminApproved;
      toast({
        title: "Invite sent",
        description: isAdminSender
          ? "Invite sent! Waiting for them to accept."
          : "Invite sent! Waiting for approval.",
      });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const handleViewReceipt = async (expenseId: string) => {
    setReceiptExpenseId(expenseId);
    setReceiptLoading(true);
    setReceiptData(null);
    try {
      const res = await apiRequest("GET", `/api/expenses/${expenseId}/receipt`);
      const data = await res.json();
      setReceiptData(data);
    } catch {
      setReceiptData(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleGhostInvite = async () => {
    if (!ghostInviteMember || !ghostInviteEmail.trim()) return;
    setGhostInviting(true);
    try {
      const res = await apiRequest("POST", `/api/ghost/${ghostInviteMember.id}/invite`, {
        email: ghostInviteEmail.trim().toLowerCase(),
      });
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setGhostInviteMember(null);
      setGhostInviteEmail("");
      if (data.merged) {
        toast({ title: "Merged!", description: `${ghostInviteMember.name} was linked to existing user ${data.userName}.` });
      } else {
        toast({ title: "Invite sent!", description: `Signup invite sent to ${data.email}.` });
      }
    } catch (err: any) {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setGhostInviting(false);
    }
  };

  // Pending invites for this group
  const { data: pendingInvites = [] } = useQuery<any[]>({
    queryKey: ["/api/groups", groupId, "invites"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/groups/${groupId}/invites`);
      return res.json();
    },
  });

  const approveInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest("POST", `/api/invites/${inviteId}/admin-approve`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "invites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      toast({ title: "Invite approved" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const rejectInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest("POST", `/api/invites/${inviteId}/admin-reject`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "invites"] });
      toast({ title: "Invite rejected" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const groupSettleUpMutation = useMutation({
    mutationFn: async () => {
      // Create a settlement expense in this group
      // paidById = the person making the payment (settling their debt)
      // splitAmongIds = the person receiving the payment
      const res = await apiRequest("POST", "/api/expenses", {
        description: "Settlement payment",
        amount: parseFloat(settleAmount),
        paidById: settlePayerId,
        splitAmongIds: [settleReceiverId],
        groupId,
        date: new Date().toISOString(),
        isSettlement: true,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      setSettleUpOpen(false);
      setSettlePayerId("");
      setSettleReceiverId("");
      setSettleAmount("");
      toast({ title: "Settled up", description: "Payment recorded in this group" });
      maybeAskForReview("settle_up");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/groups/${groupId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      setDeleteGroupStep(0);
      toast({ title: "Group deleted" });
      setLocation("/groups");
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const exportGroupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/export/expenses", {
        scope: "group",
        groupId,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Export sent", description: data.message || "CSV sent to your email" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      await apiRequest("DELETE", `/api/groups/${groupId}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setMemberActionMember(null);
      toast({ title: "Member removed" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/promote/${memberId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setMemberActionMember(null);
      toast({ title: "Member promoted to Admin" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const demoteMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/demote/${memberId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setMemberActionMember(null);
      toast({ title: "Admin demoted to Member" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const leaveGroupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/leave`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      toast({ title: "You left the group" });
      setLocation("/groups");
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Cannot leave group", description: msg, variant: "destructive" });
    },
  });

  const deleteAllExpensesMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/groups/${groupId}/expenses`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      toast({ title: "All expenses deleted" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  // ===== Shareable invite link (V1) =====
  // Fetch the active invite link only when the dialog is open (avoids unnecessary requests)
  const { data: activeInviteLinkData } = useQuery<{ link: { id: string; code: string; expiresAt: string } | null }>({
    queryKey: [`/api/groups/${groupId}/invite-link`],
    enabled: shareLinkOpen,
  });
  const activeInviteLink = activeInviteLinkData?.link ?? null;
  const inviteLinkUrl = activeInviteLink
    ? inviteUrl(activeInviteLink.code)
    : null;

  const generateInviteLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/invite-link`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/groups/${groupId}/invite-link`] });
      setShareLinkCopied(false);
      toast({ title: "Invite link generated" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Failed to generate link", description: msg, variant: "destructive" });
    },
  });

  const revokeInviteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      await apiRequest("DELETE", `/api/groups/${groupId}/invite-link/${linkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/groups/${groupId}/invite-link`] });
      setShareLinkCopied(false);
      toast({ title: "Invite link revoked" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Failed to revoke link", description: msg, variant: "destructive" });
    },
  });

  // Native share sheet for the group invite link. Falls back to clipboard if
  // the OS share API isn't available (rare — works in iOS WKWebView, Android
  // Chrome/TWA, and modern mobile browsers). Triggers the "where do you want
  // to send this?" sheet showing every messaging app the user has installed.
  const handleCopyShareLink = async () => {
    if (!inviteLinkUrl) return;
    const result = await shareInviteLink({
      groupName: group?.name || "this group",
      inviterName: user?.name || "A friend",
      url: inviteLinkUrl,
    });
    if (result.method === "clipboard") {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } else if (result.method === "error") {
      toast({ title: "Couldn't share", description: "Long-press the link to copy manually.", variant: "destructive" });
    }
  };

  // Instant-add an existing Spliiit user by email. Server enforces owner/admin
  // permission and pushes a notification to the added user.
  const quickAddMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/groups/${groupId}/members/add-by-email`, {
        email: quickAddEmail.trim().toLowerCase(),
      });
      return res.json();
    },
    onSuccess: (data: { addedUser: { name: string } }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/members/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      track("group_member_added_by_email", { groupId });
      toast({
        title: `${data.addedUser.name} added`,
        description: "They've been notified.",
      });
      setQuickAddEmail("");
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Couldn't add", description: msg, variant: "destructive" });
    },
  });

  // Balance calculations
  const balances = calculateGroupBalances(expenses);
  const simplifiedSettlements = simplifyDebts(balances);
  const pairwiseSettlements = calculatePairwiseBalances(expenses);

  // Personal view: only settlements involving the current user
  const myPairwise = pairwiseSettlements.filter(
    s => s.from === user?.id || s.to === user?.id
  );
  const mySimplified = simplifiedSettlements.filter(
    s => s.from === user?.id || s.to === user?.id
  );
  const getPersonName = (id: string) => {
    if (id === user?.id) return "You";
    return members.find((m) => m.id === id)?.name || "Someone";
  };
  // Clamp all avatars to the warm cream/terracotta palette (matches the rest of the app).
  const WARM_AVATARS = ["#7A3E32", "#8C5A3C", "#9A4A2A", "#A6674A", "#8A6A32", "#B04A34", "#6B4A3A", "#B5794A"];
  const warmAvatar = (id: string) => { let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0; return WARM_AVATARS[h % WARM_AVATARS.length]; };
  const getPersonColor = (id: string) => warmAvatar(id);

  // DISPLAY-ONLY: pick a color class for an expense row's amount based on the
  // current user's direction on it. Does NOT touch any balance/expense
  // calculation — it only reads paidById / splitAmongIds that are already on
  // the row and returns a CSS class. The dollar value shown is unchanged.
  //   - regular expense you paid     → green (you'll be reimbursed)
  //   - regular expense you owe into → red   (it costs you)
  //   - settlement you paid out      → red
  //   - settlement you received      → green
  //   - not involved / personal      → neutral (default text)
  const expenseAmountColor = (expense: any): string => {
    const uid = user?.id;
    if (!uid) return "text-muted-foreground";
    const inSplit = Array.isArray(expense.splitAmongIds) && expense.splitAmongIds.includes(uid);
    const iPaid = expense.paidById === uid;
    if (expense.isSettlement) {
      if (iPaid) return AMOUNT_OUT_CLASS;   // you handed money over → red
      if (inSplit) return AMOUNT_IN_CLASS;  // you received money → ink
      return "text-muted-foreground";       // not your settlement → recede
    }
    if (iPaid) {
      // Personal expense (only you in the split) = no money moves → recede.
      if (inSplit && expense.splitAmongIds.length === 1) return "text-muted-foreground";
      return AMOUNT_IN_CLASS;               // others owe you → ink
    }
    if (inSplit) return AMOUNT_OUT_CLASS;    // you owe your share → red
    return "text-muted-foreground";          // not involved → recede
  };

  // Role helpers
  const adminIds = group?.adminIds || [];
  const isMeOwner = group?.createdById === user?.id;
  const isMeAdmin = adminIds.includes(user?.id || "");
  const isMeGlobalAdmin = user?.isAdmin;

  const getMemberRole = (memberId: string): "owner" | "admin" | "member" => {
    if (memberId === group?.createdById) return "owner";
    if ((group?.adminIds || []).includes(memberId)) return "admin";
    return "member";
  };

  // Whether current user can perform actions on a given member
  const canActOnMember = (memberId: string): boolean => {
    if (!group) return false;
    if (memberId === user?.id) return false; // Can't act on yourself via member dialog
    return isMeOwner || isMeAdmin || !!isMeGlobalAdmin;
  };

  const totalGroupSpend = expenses.reduce((sum, e) => sum + e.amount, 0);

  if (!group) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 bg-muted animate-pulse rounded" />
        <div className="h-20 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header — one row: back, group name, ⋮, and a round + (add
          expense). The icon-only + leaves the name room for two lines. */}
      <div className="flex items-center gap-2">
        <Link href="/groups">
          <Button size="icon" variant="ghost" data-testid="back-to-groups">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          {renaming ? (
            <form className="flex items-center gap-1" onSubmit={async (e) => {
              e.preventDefault();
              const trimmed = newGroupName.trim();
              if (!trimmed) { setRenaming(false); return; }
              try {
                await apiRequest("PATCH", `/api/groups/${groupId}/name`, { name: trimmed });
                queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
                queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
                toast({ title: "Group renamed" });
              } catch (err: any) {
                let msg = err.message;
                try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
                toast({ title: "Error", description: msg, variant: "destructive" });
              }
              setRenaming(false);
            }}>
              <Input
                autoFocus
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="h-8 text-lg font-semibold"
                onBlur={() => setRenaming(false)}
                onKeyDown={(e) => { if (e.key === "Escape") setRenaming(false); }}
              />
            </form>
          ) : (
            <h1
              className={`text-[26px] leading-[1.05] tracking-tight line-clamp-2 break-words font-serif ${(isMeOwner || isMeAdmin || isMeGlobalAdmin) ? "cursor-pointer" : ""}`}
              onClick={() => {
                if (isMeOwner || isMeAdmin || isMeGlobalAdmin) {
                  setNewGroupName(group.name);
                  setRenaming(true);
                }
              }}
            >
              {group.name}
              {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
                <Pencil className="w-3.5 h-3.5 inline ml-2 text-muted-foreground align-middle" />
              )}
            </h1>
          )}
        </div>
        {/* Three-dots group actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="shrink-0" data-testid="group-actions-menu">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
              <DropdownMenuItem onClick={() => { setNewGroupName(group.name); setRenaming(true); }}>
                <Pencil className="w-4 h-4 mr-2" /> Rename Group
              </DropdownMenuItem>
            )}
            {/* Invite link — V1: any group member can generate (auto-revokes prior link) */}
            <DropdownMenuItem onClick={() => setShareLinkOpen(true)} data-testid="menu-invite-link">
              <Share2 className="w-4 h-4 mr-2" /> Invite link
            </DropdownMenuItem>
            {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
              <DropdownMenuItem onClick={() => { setImportStep("upload"); setImportFile(null); setImportResult(null); setImportOpen(true); }}>
                <Upload className="w-4 h-4 mr-2" /> Import from Splitwise
              </DropdownMenuItem>
            )}
            {expenses.length > 0 && (
              <DropdownMenuItem onClick={() => exportGroupMutation.mutate()} disabled={exportGroupMutation.isPending}>
                <Download className="w-4 h-4 mr-2" /> Export Expenses
              </DropdownMenuItem>
            )}
            {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && expenses.length > 0 && (
              <DropdownMenuItem onClick={async () => {
                try {
                  await apiRequest("PATCH", `/api/groups/${groupId}/simplify-debts`, { simplifyDebts: !group.simplifyDebts });
                  queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] });
                } catch {}
              }}>
                <Shuffle className="w-4 h-4 mr-2" /> {group.simplifyDebts ? "Simplify: ON" : "Simplify: OFF"}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => leaveGroupMutation.mutate()} disabled={leaveGroupMutation.isPending}>
              <LogOut className="w-4 h-4 mr-2" /> Leave Group
            </DropdownMenuItem>
            {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && expenses.length > 0 && (
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => {
                if (confirm(`Delete all ${expenses.length} expenses in this group? This cannot be undone.`)) {
                  deleteAllExpensesMutation.mutate();
                }
              }}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete All Expenses
              </DropdownMenuItem>
            )}
            {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteGroupStep(1)}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete Group
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Shareable invite link dialog (V1) */}
        <Dialog open={shareLinkOpen} onOpenChange={setShareLinkOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Group invite link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!activeInviteLink ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    No active invite link for this group yet. Generate one to share with people you want to add.
                  </p>
                  <Button
                    className="w-full"
                    onClick={() => generateInviteLinkMutation.mutate()}
                    disabled={generateInviteLinkMutation.isPending}
                    data-testid="btn-generate-invite-link"
                  >
                    {generateInviteLinkMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Share2 className="w-4 h-4 mr-2" /> Generate invite link</>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Anyone with this link can join <span className="font-medium text-foreground">{group.name}</span>. Expires{" "}
                    {new Date(activeInviteLink.expiresAt).toLocaleDateString("en-US", {
                      year: "numeric", month: "long", day: "numeric"
                    })}.
                  </p>
                  <ShareChannels
                    url={inviteLinkUrl}
                    text={`Join "${group.name}" on Spliiit — tap the link to hop in:`}
                    emailSubject={`Join ${group.name} on Spliiit`}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => generateInviteLinkMutation.mutate()}
                      disabled={generateInviteLinkMutation.isPending}
                      data-testid="btn-regenerate-invite-link"
                    >
                      {generateInviteLinkMutation.isPending ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        "Generate new link"
                      )}
                    </Button>
                    {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
                      <Button
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => revokeInviteLinkMutation.mutate(activeInviteLink.id)}
                        disabled={revokeInviteLinkMutation.isPending}
                        data-testid="btn-revoke-invite-link"
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Generating a new link will revoke this one. Revoking is permanent — anyone with the old link won't be able to join.
                  </p>
                </>
              )}

              {/* Quick-add by email — for friends who already have Spliiit accounts.
                  Skips the link + accept dance entirely. Restricted server-side
                  to group owner/admin/global admin (consent guardrail). */}
              {(isMeOwner || isMeAdmin || isMeGlobalAdmin) && (
                <div className="pt-4 mt-2 border-t border-border space-y-3">
                  <div>
                    <p className="text-sm font-semibold">Already on Spliiit?</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Add them by email — no link needed. They'll get a notification.
                    </p>
                  </div>
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (quickAddEmail.trim()) quickAddMutation.mutate();
                    }}
                  >
                    <Input
                      type="email"
                      placeholder="friend@example.com"
                      value={quickAddEmail}
                      onChange={(e) => setQuickAddEmail(e.target.value)}
                      className="flex-1"
                      disabled={quickAddMutation.isPending}
                      data-testid="input-quick-add-email"
                    />
                    <Button
                      type="submit"
                      disabled={!quickAddEmail.trim() || quickAddMutation.isPending}
                      data-testid="btn-quick-add-member"
                    >
                      {quickAddMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><UserPlus className="w-4 h-4 mr-1.5" /> Add</>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Button size="icon" className="rounded-full shrink-0" aria-label="Add expense" data-testid="add-expense-btn" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4" />
        </Button>
        <AddExpenseSheet
          open={addOpen}
          onOpenChange={(o) => { setAddOpen(o); if (!o) setAddDraft(null); }}
          people={members.map((m) => ({ id: m.id, name: m.name, isGhost: (m as any).isGhost }))}
          groupId={groupId}
          draft={addDraft}
          invalidateKeys={[["/api/expenses/group", groupId], ["/api/groups", groupId, "history"], ["/api/groups", groupId, "activity"]]}
        />
      </div>

      {/* Members — one fixed-width button (with a 3-face peek) so the row never overflows,
          no matter how many members. Tap opens the full members sheet. */}
      <div className="flex items-center gap-3 mb-1">
        <button
          type="button"
          onClick={() => setMembersSheetOpen(true)}
          className="flex items-center gap-2.5 bg-foreground text-background rounded-full pl-2 pr-3.5 py-1.5 shrink-0 hover-elevate"
          data-testid="members-button"
        >
          <div className="flex">
            {members.slice(0, 3).map((m, i) => {
              const isGhost = (m as any).isGhost;
              return (
                <div
                  key={m.id}
                  className="w-[26px] h-[26px] rounded-full border-2 border-foreground flex items-center justify-center text-white text-[11px] font-semibold"
                  style={{ marginLeft: i === 0 ? 0 : -9, backgroundColor: isGhost ? "hsl(38 38% 45%)" : warmAvatar(m.id) }}
                >
                  {isGhost ? <Ghost className="w-3 h-3" /> : m.name[0]?.toUpperCase()}
                </div>
              );
            })}
          </div>
          <span className="text-sm font-semibold whitespace-nowrap">{members.length} members</span>
          <ChevronRight className="w-4 h-4 opacity-70" />
        </button>
        <button
          className="flex items-center gap-1.5 text-sm font-medium text-accent-foreground shrink-0 ml-auto"
          data-testid="invite-member-btn"
          onClick={() => setShareLinkOpen(true)}
        >
          <UserPlus className="w-4 h-4" /> Invite
        </button>

        {/* Full members sheet — a roster with email under each name (disambiguates duplicate
            names), and preserves every per-member tap action (ghost invite / manage / info). */}
        <Dialog open={membersSheetOpen} onOpenChange={setMembersSheetOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{members.length} members</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col pt-1 max-h-[62vh] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {members.map((m) => {
                const role = getMemberRole(m.id);
                const canAct = canActOnMember(m.id);
                const isGhost = (m as any).isGhost;
                const email = (m as any).email as string | undefined;
                const tappable = isGhost || canAct || m.id !== user?.id;
                // The kebab opens the same per-member popup the row tap used to:
                // ghost → invite, actionable → manage (remove / promote), else info.
                const openActions = () => {
                  setMembersSheetOpen(false);
                  if (isGhost) { setGhostInviteMember(m); setGhostInviteEmail(""); }
                  else if (canAct) setMemberActionMember(m);
                  else if (m.id !== user?.id) setMemberInfoMember(m);
                };
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 py-2.5 border-b border-border last:border-b-0"
                    data-testid={`member-row-${m.id}`}
                  >
                    <div className="relative shrink-0">
                      <div
                        className={`w-10 h-10 rounded-full border-2 border-background flex items-center justify-center text-white text-sm font-semibold ${isGhost ? "border-dashed !border-amber-500/50" : ""}`}
                        style={{ backgroundColor: isGhost ? "transparent" : warmAvatar(m.id) }}
                      >
                        {isGhost ? <Ghost className="w-4 h-4 text-amber-500" /> : m.name[0]?.toUpperCase()}
                      </div>
                      {!isGhost && role === "owner" && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-yellow-500 border-2 border-background flex items-center justify-center" title="Owner">
                          <Crown className="w-2 h-2 text-white" />
                        </span>
                      )}
                      {!isGhost && role === "admin" && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary border-2 border-background flex items-center justify-center" title="Admin">
                          <Shield className="w-2 h-2 text-white" />
                        </span>
                      )}
                      {isGhost && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center" title="Ghost — tap to invite">
                          <Mail className="w-2 h-2 text-white" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold truncate">
                        {m.id === user?.id ? "You" : m.name}
                        {role === "owner" && <span className="text-muted-foreground font-medium"> · host</span>}
                        {role === "admin" && <span className="text-muted-foreground font-medium"> · admin</span>}
                      </p>
                      {isGhost ? (
                        <p className="font-mono text-[11px] text-amber-600 mt-0.5">Ghost · not joined yet</p>
                      ) : email ? (
                        <p className="font-mono text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
                      ) : null}
                    </div>
                    {tappable && (
                      <button
                        type="button"
                        onClick={openActions}
                        className="shrink-0 h-9 w-9 -mr-1.5 rounded-full flex items-center justify-center text-muted-foreground hover-elevate"
                        aria-label={`Options for ${m.id === user?.id ? "you" : m.name}`}
                        data-testid={`member-menu-${m.id}`}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a Friend</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4 pt-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (inviteEmail.trim()) inviteMutation.mutate();
              }}
            >
              <div className="space-y-2">
                <Label>Friend's Email</Label>
                <Input
                  type="email"
                  placeholder="friend@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="input-invite-email"
                />
                <p className="text-xs text-muted-foreground">
                  They need a Spliiit account to accept. Not on Spliiit yet?
                </p>
                <div className="flex flex-wrap gap-2 pt-0.5">
                  {/* PRIMARY: native share sheet — one tap to IG/WhatsApp/iMessage/etc. */}
                  <button
                    type="button"
                    onClick={async () => {
                      const result = await shareAppLink({
                        inviterName: user?.name || "A friend",
                        referralCode: user?.referralCode,
                      });
                      if (result.method === "clipboard") {
                        setInviteLinkCopied(true);
                        setTimeout(() => setInviteLinkCopied(false), 2000);
                      }
                    }}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    {inviteLinkCopied ? "Link copied!" : "Share invite"}
                  </button>
                  {/* SECONDARY: explicit WhatsApp shortcut */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Hey, ${user?.name || "a friend"} here. I use Spliiit to split expenses with friends — it's free with no limits. Join me: https://spliiit.klarityit.ca${user?.referralCode ? `?ref=${user.referralCode}` : ""}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/60 transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-foreground" />
                    WhatsApp
                  </a>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={!inviteEmail.trim() || inviteMutation.isPending}
                data-testid="submit-invite"
              >
                {inviteMutation.isPending ? "Sending..." : "Send Invite"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invites Section */}
      {pendingInvites.length > 0 && (() => {
        const adminIds = group?.adminIds || [];
        const isOwnerOrAdmin = group && (
          group.createdById === user?.id ||
          adminIds.includes(user?.id || "") ||
          user?.isAdmin
        );
        return (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-amber-500">Pending Invites</h3>
            {pendingInvites.map((invite: any) => {
              const waitingAdmin = !invite.adminApproved;
              const waitingInvitee = invite.adminApproved && !invite.inviteeAccepted;
              return (
                <div
                  key={invite.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2"
                  data-testid={`pending-invite-${invite.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {invite.inviteeName}
                      <span className="text-muted-foreground font-normal"> invited by {invite.inviterName}</span>
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {waitingAdmin && (
                        <>
                          <Clock className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-amber-500">Waiting for admin approval</span>
                        </>
                      )}
                      {waitingInvitee && (
                        <>
                          <Clock className="w-3 h-3 text-blue-400" />
                          <span className="text-xs text-blue-400 min-w-0 truncate">Waiting for {invite.inviteeName} to accept</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isOwnerOrAdmin && waitingAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-foreground hover:text-green-400 hover:bg-green-500/10"
                        onClick={() => approveInviteMutation.mutate(invite.id)}
                        disabled={approveInviteMutation.isPending}
                        data-testid={`approve-invite-${invite.id}`}
                        title="Approve invite"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-red-400 hover:bg-destructive/10"
                        onClick={() => rejectInviteMutation.mutate(invite.id)}
                        disabled={rejectInviteMutation.isPending}
                        data-testid={`reject-invite-${invite.id}`}
                        title="Reject invite"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Your Balances + Settle Up */}
      {expenses.length > 0 && (() => {
        const myBal = balances.find(b => b.personId === user?.id);
        const net = myBal ? Math.round(myBal.amount * 100) / 100 : 0;
        const settledUp = isEffectivelySettled(net);
        // Phantom-cent settlements (< $0.05) are filtered so we don't show "$0.01" rows.
        const mySettlements = (group.simplifyDebts ? mySimplified : myPairwise)
          .filter((s) => !isEffectivelySettled(s.amount));
        return (
          <div>
            {/* Balance hero */}
            {settledUp ? (
              <p className="font-serif text-4xl text-muted-foreground tracking-tight mb-5">All settled up</p>
            ) : (
              <div className="mb-5">
                <p className="text-sm text-muted-foreground mb-1">{net > 0 ? "You're owed in this group" : "You owe in this group"}</p>
                <FitText max={48} className={`font-serif leading-none tracking-tight ${net > 0 ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS}`}>
                  {formatMoney(Math.abs(net), userCurrency)}
                </FitText>
              </div>
            )}

            {/* Settle up */}
            {!settledUp && (
              <Button
                className="w-full rounded-full mb-6"
                onClick={() => setSettleUpOpen(true)}
                data-testid="group-settle-up-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Settle up
              </Button>
            )}

            {/* Your balances — per person (amount colour carries the direction) */}
            {mySettlements.length > 0 && (
              <div className="mb-6">
                <h2 className="font-serif text-2xl tracking-tight mb-3">
                  {group.simplifyDebts ? "Simplified" : "Your balances"}
                </h2>
                <div className="flex flex-col gap-3">
                  {mySettlements.map((s, i) => {
                    const youOwe = s.from === user?.id;
                    const otherPerson = youOwe ? s.to : s.from;
                    return (
                      <Card key={i} className="p-[18px] rounded-[22px] flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-semibold shrink-0"
                          style={{ backgroundColor: warmAvatar(otherPerson) }}
                        >
                          {getPersonName(otherPerson)[0]?.toUpperCase()}
                        </div>
                        <span className="flex-1 min-w-0 text-[16px] font-semibold truncate">{getPersonName(otherPerson)}</span>
                        <span className={`text-base font-semibold shrink-0 font-mono ${youOwe ? AMOUNT_OUT_CLASS : AMOUNT_IN_CLASS}`}>
                          {formatMoney(s.amount, userCurrency)}
                        </span>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Member Balances — collapsible. Default hidden so the group view
          stays tidy on large groups (e.g., 10-person trips). The user's
          own balance is already shown above in the "You owe X" card —
          this section is the everyone-else breakdown, opt-in via tap. */}
      {balances.filter(b => b.personId !== user?.id).length > 0 && (
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between"
            onClick={() => setMemberBalancesOpen(o => !o)}
            data-testid="member-balances-toggle"
            aria-expanded={memberBalancesOpen}
          >
            <span className="flex items-center gap-1.5">
              <Users2 className="w-4 h-4" />
              Member Balances
              <span className="text-xs text-muted-foreground font-normal ml-1">
                ({balances.filter(b => b.personId !== user?.id).length})
              </span>
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${memberBalancesOpen ? "rotate-180" : ""}`}
            />
          </Button>
          {memberBalancesOpen && (
            <div className="space-y-1.5 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
              {balances.filter(b => b.personId !== user?.id).map((b) => {
                // Snap sub-cent residuals to 0 for display ("settled" state)
                const displayAmt = displayBalance(b.amount);
                return (
                  <div key={b.personId} className="flex items-center justify-between gap-2 px-1 py-0.5">
                    <span className="text-base min-w-0 truncate">{getPersonName(b.personId)}</span>
                    <span
                      className={`text-base font-semibold shrink-0 whitespace-nowrap ${
                        displayAmt > 0 ? AMOUNT_IN_CLASS : displayAmt < 0 ? AMOUNT_OUT_CLASS : "text-muted-foreground"
                      }`}
                    >
                      <span className="text-xs font-normal opacity-80">
                        {displayAmt > 0 ? "gets back" : displayAmt < 0 ? "pays" : "settled"}
                      </span>{" "}
                      {displayAmt !== 0 && formatMoney(Math.abs(displayAmt), userCurrency)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Group Settle Up Dialog */}
      <Dialog open={settleUpOpen} onOpenChange={(open) => { setSettleUpOpen(open); if (!open) { setSettlePayerId(""); setSettleReceiverId(""); setSettleAmount(""); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Settle Up in {group.name}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4 pt-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (settlePayerId && settleReceiverId && settleAmount && settlePayerId !== settleReceiverId) {
                groupSettleUpMutation.mutate();
              }
            }}
          >
            <div className="space-y-2">
              <Label>Who is paying?</Label>
              <Select value={settlePayerId} onValueChange={setSettlePayerId}>
                <SelectTrigger data-testid="settle-payer-select">
                  <SelectValue placeholder="Select who is paying" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.id === user?.id ? `${m.name} (You)` : m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Paying to</Label>
              <Select value={settleReceiverId} onValueChange={setSettleReceiverId}>
                <SelectTrigger data-testid="settle-receiver-select">
                  <SelectValue placeholder="Select who receives" />
                </SelectTrigger>
                <SelectContent>
                  {members
                    .filter((m) => m.id !== settlePayerId)
                    .map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.id === user?.id ? `${m.name} (You)` : m.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                data-testid="settle-amount-input"
              />
            </div>
            {settlePayerId && settleReceiverId && settleAmount && (
              <div className="rounded-lg bg-muted/50 p-3 text-center space-y-1">
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{getPersonName(settlePayerId)}</span>
                  {" pay "}
                  <span className="font-semibold text-foreground">{getPersonName(settleReceiverId)}</span>
                </p>
                <p className="text-xl font-bold text-primary">
                  {formatMoney(parseFloat(settleAmount) || 0, userCurrency)}
                </p>
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={
                !settlePayerId ||
                !settleReceiverId ||
                !settleAmount ||
                settlePayerId === settleReceiverId ||
                groupSettleUpMutation.isPending
              }
              data-testid="confirm-group-settle-up"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {groupSettleUpMutation.isPending ? "Settling..." : "Confirm Settlement"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog open={!!deleteExpenseId} onOpenChange={(open) => { if (!open) setDeleteExpenseId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-4">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-foreground">
                Are you sure you want to delete this expense? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteExpenseId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  if (deleteExpenseId) deleteExpenseMutation.mutate(deleteExpenseId);
                }}
                disabled={deleteExpenseMutation.isPending}
                data-testid="confirm-delete-group-expense"
              >
                {deleteExpenseMutation.isPending ? "Deleting..." : "Yes, Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History — expenses by month, plus what changed: deleted ones stay
          in place (struck, with Restore) and edits appear as their own entry.
          Tap a live row for its detail (dates, edit, receipt). */}
      <div>
        <h2 className="font-serif text-2xl tracking-tight mb-3">History</h2>
        <ExpenseHistory
          source={{ groupId }}
          liveExpenses={expenses}
          meId={user?.id ?? ""}
          nameOf={getPersonName}
          onOpenDeleted={(e) => setDetailExpense(e)}
          emptyState={
            <Card className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-base font-semibold mb-1">No expenses yet</h3>
              <p className="text-sm text-muted-foreground">
                Any group member can add expenses. Start splitting.
              </p>
            </Card>
          }
          renderLive={(expense) => {
            const d = new Date(expense.date);
            return (
              <div
                className="flex items-center gap-4 py-4 border-b border-border cursor-pointer"
                onClick={() => setDetailExpense(expense)}
                data-testid={`expense-card-${expense.id}`}
              >
                <div className="w-11 text-center shrink-0">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {d.toLocaleDateString(undefined, { month: "short" })}
                  </div>
                  <div className="font-serif text-2xl leading-none">{d.getDate()}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold tracking-tight truncate">
                    {expense.isSettlement ? "Settlement" : expense.description}
                    {expense.updatedAt && <EditedTag />}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground truncate mt-0.5">
                    {expense.isSettlement
                      ? `${getPersonName(expense.paidById)} paid ${getPersonName(expense.splitAmongIds[0])}`
                      : `${getPersonName(expense.paidById)} paid · split ${expense.splitAmongIds.length} ways`}
                                      </p>
                </div>
                <ExpenseAmount
                  amount={expense.amount}
                  currency={expense.currency}
                  baseCurrency={expense.baseCurrency}
                  originalAmount={expense.originalAmount}
                  viewerCurrency={userCurrency}
                  className={`font-mono text-base font-semibold ${expenseAmountColor(expense)}`}
                />
                {canDeleteExpense(expense) && (
                  <button
                    className="text-muted-foreground shrink-0 p-1 opacity-60"
                    onClick={(e) => { e.stopPropagation(); setDeleteExpenseId(expense.id); }}
                    data-testid={`delete-group-expense-${expense.id}`}
                    aria-label="Delete expense"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          }}
        />
      </div>

      <EditExpenseSheet
        expense={editExpense}
        open={!!editExpense}
        onClose={() => setEditExpense(null)}
        payerOptions={members.map((m) => ({ id: m.id, name: m.id === user?.id ? "You" : m.name }))}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] });
          queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
          queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "history"] });
        }}
      />

      {/* Activity Feed — hidden for now, data collecting silently via groupActivity query */}
      {/* Will be surfaced when we have a dedicated audit/history view */}

      {/* Expense Detail Dialog */}
      <Dialog open={!!detailExpense} onOpenChange={(open) => { if (!open) setDetailExpense(null); }}>
        {/* Don't auto-focus the first button (Edit / Restore): phones draw a
            focus ring on it as soon as the detail opens. */}
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{detailExpense?.isSettlement ? "Settlement" : detailExpense?.description}</DialogTitle>
          </DialogHeader>
          {detailExpense && (
            <div className="space-y-4 pt-2">
              {/* Amount + Date */}
              <div className="flex items-start justify-between gap-3">
                <ExpenseAmount
                  amount={detailExpense.amount}
                  currency={detailExpense.currency}
                  baseCurrency={detailExpense.baseCurrency}
                  originalAmount={detailExpense.originalAmount}
                  viewerCurrency={userCurrency}
                  className="text-2xl font-bold text-primary text-left min-w-0"
                />
                <span className="text-sm text-muted-foreground font-mono shrink-0 pt-1.5">{new Date(detailExpense.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
              </div>

              {/* Paid by */}
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs text-muted-foreground mb-1">Paid by</p>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
                    style={{ backgroundColor: getPersonColor(detailExpense.paidById) }}
                  >
                    {getPersonName(detailExpense.paidById)[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{getPersonName(detailExpense.paidById)}</span>
                </div>
              </div>

              {/* Split between */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {detailExpense.isSettlement ? "Paid to" : `Split between ${detailExpense.splitAmongIds.length} ${detailExpense.splitAmongIds.length === 1 ? "person" : "people"}`}
                </p>
                <div className="space-y-1.5">
                  {(() => {
                    let customSplits: Record<string, number> | null = null;
                    if ((detailExpense as any).splitAmounts) {
                      try { customSplits = JSON.parse((detailExpense as any).splitAmounts); } catch {}
                    }
                    return detailExpense.splitAmongIds.map((personId: string) => {
                      const share = customSplits
                        ? (customSplits[personId] || 0)
                        : detailExpense.amount / detailExpense.splitAmongIds.length;
                      return (
                        <div key={personId} className="flex items-center justify-between gap-2 py-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
                              style={{ backgroundColor: getPersonColor(personId) }}
                            >
                              {getPersonName(personId)[0]?.toUpperCase()}
                            </div>
                            <span className="text-sm truncate">{getPersonName(personId)}</span>
                          </div>
                          <span className="text-sm font-medium shrink-0 whitespace-nowrap">{formatMoney(share, userCurrency)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Notes */}
              {(detailExpense as any).notes && (
                <div className="rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground mb-1">Note</p>
                  <p className="text-sm break-words [overflow-wrap:anywhere]">{(detailExpense as any).notes}</p>
                </div>
              )}

              {/* Spent on / Posted (locked) / Last edited */}
              <ExpenseStory
                expense={detailExpense}
                source={{ groupId }}
                meId={user?.id ?? ""}
                isAdmin={!!user?.isAdmin}
                nameOf={getPersonName}
                onRestored={() => {
                  setDetailExpense(null);
                  queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] });
                  queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
                }}
              />
              {!detailExpense.deletedAt && canDeleteExpense(detailExpense) && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { setEditExpense(detailExpense); setDetailExpense(null); }}
                  data-testid="edit-group-expense"
                >
                  Edit expense
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Group 2-step Confirmation Dialog */}
      <Dialog open={deleteGroupStep > 0} onOpenChange={(open) => { if (!open) setDeleteGroupStep(0); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleteGroupStep === 1 ? "Delete Group" : "Final Warning"}
            </DialogTitle>
          </DialogHeader>
          {deleteGroupStep === 1 && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-4">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                <p className="text-sm text-foreground">
                  Are you sure you want to delete <strong>{group.name}</strong>?
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteGroupStep(0)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => setDeleteGroupStep(2)}
                >
                  Yes, Delete
                </Button>
              </div>
            </div>
          )}
          {deleteGroupStep === 2 && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg bg-destructive/10 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                  <p className="text-sm font-semibold text-destructive">This is permanent</p>
                </div>
                <p className="text-sm text-foreground">
                  If you delete <strong>{group.name}</strong>, all expense history in this group will also be removed. This cannot be undone.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteGroupStep(0)}
                >
                  No, keep group
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => deleteGroupMutation.mutate()}
                  disabled={deleteGroupMutation.isPending}
                  data-testid="confirm-delete-group-final"
                >
                  {deleteGroupMutation.isPending ? "Deleting..." : "I understand, delete"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Member Action Dialog */}
      <Dialog open={!!memberActionMember} onOpenChange={(open) => { if (!open) setMemberActionMember(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {memberActionMember?.name}
              {memberActionMember && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {getMemberRole(memberActionMember.id) === "owner" ? "Owner" : getMemberRole(memberActionMember.id) === "admin" ? "Admin" : "Member"}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {memberActionMember && (
            <div className="space-y-2 pt-2">
              {/* How this member wants to get paid */}
              {memberActionMember.id !== user?.id && (
                <div className="rounded-lg border border-border p-3 mb-1">
                  <PaymentMethodsView userId={memberActionMember.id} name={memberActionMember.name} compact />
                </div>
              )}
              {/* Promote to Admin — owner only, for members */}
              {(isMeOwner || isMeGlobalAdmin) && getMemberRole(memberActionMember.id) === "member" && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => promoteMutation.mutate(memberActionMember.id)}
                  disabled={promoteMutation.isPending}
                  data-testid={`promote-member-${memberActionMember.id}`}
                >
                  <Shield className="w-4 h-4 mr-2 text-primary" />
                  {promoteMutation.isPending ? "Promoting..." : "Promote to Admin"}
                </Button>
              )}
              {/* Demote to Member — owner only, for admins */}
              {(isMeOwner || isMeGlobalAdmin) && getMemberRole(memberActionMember.id) === "admin" && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => demoteMutation.mutate(memberActionMember.id)}
                  disabled={demoteMutation.isPending}
                  data-testid={`demote-member-${memberActionMember.id}`}
                >
                  <UserMinus className="w-4 h-4 mr-2 text-muted-foreground" />
                  {demoteMutation.isPending ? "Demoting..." : "Demote to Member"}
                </Button>
              )}
              {/* Remove from Group — owner/admin, not for owner or other admins (unless you're owner) */}
              {getMemberRole(memberActionMember.id) !== "owner" &&
                (isMeOwner || isMeGlobalAdmin || (isMeAdmin && getMemberRole(memberActionMember.id) === "member")) && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => removeMemberMutation.mutate(memberActionMember.id)}
                  disabled={removeMemberMutation.isPending}
                  data-testid={`remove-member-${memberActionMember.id}`}
                >
                  <X className="w-4 h-4 mr-2" />
                  {removeMemberMutation.isPending ? "Removing..." : "Remove from Group"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt detail dialog */}
      <Dialog open={!!receiptExpenseId} onOpenChange={(open) => { if (!open) { setReceiptExpenseId(null); setReceiptData(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {receiptData?.merchant || "Receipt Details"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            {receiptLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground ml-2">Loading receipt...</span>
              </div>
            ) : receiptData ? (
              <>
                {receiptData.items?.length > 0 && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {receiptData.items.map((item: any, i: number) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-muted/30" : ""}>
                            <td className="px-3 py-2 text-foreground">{item.name}</td>
                            <td className="px-3 py-2 text-right font-medium text-foreground whitespace-nowrap">
                              ${typeof item.price === "number" ? item.price.toFixed(2) : item.price}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="border-t border-border px-3 py-2 space-y-1">
                      {receiptData.subtotal != null && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Subtotal</span>
                          <span>${receiptData.subtotal.toFixed(2)}</span>
                        </div>
                      )}
                      {receiptData.tax != null && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Tax</span>
                          <span>${receiptData.tax.toFixed(2)}</span>
                        </div>
                      )}
                      {receiptData.total != null && (
                        <div className="flex justify-between text-sm font-semibold text-foreground pt-1 border-t border-border">
                          <span>Total</span>
                          <span>${receiptData.total.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {(!receiptData.items || receiptData.items.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No items extracted from this receipt.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No receipt data available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Member payment-info dialog — shown when a non-admin taps a member */}
      <Dialog open={!!memberInfoMember} onOpenChange={(open) => { if (!open) setMemberInfoMember(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{memberInfoMember?.name}</DialogTitle>
          </DialogHeader>
          {memberInfoMember && (
            <div className="pt-1">
              <PaymentMethodsView userId={memberInfoMember.id} name={memberInfoMember.name} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ghost invite dialog */}
      <Dialog open={!!ghostInviteMember} onOpenChange={(open) => { if (!open) { setGhostInviteMember(null); setGhostInviteEmail(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ghost className="w-5 h-5 text-amber-500" />
              Invite {ghostInviteMember?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              <strong>{ghostInviteMember?.name}</strong> was imported from Splitwise but doesn't have a Spliiit account yet. Enter their email to send a signup invite.
            </p>
            <div className="space-y-2">
              <Label htmlFor="ghost-email">Email address</Label>
              <Input
                id="ghost-email"
                type="email"
                placeholder="friend@example.com"
                value={ghostInviteEmail}
                onChange={(e) => setGhostInviteEmail(e.target.value)}
                data-testid="ghost-invite-email"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleGhostInvite}
              disabled={ghostInviting || !ghostInviteEmail.trim() || !ghostInviteEmail.includes("@")}
              data-testid="ghost-invite-submit"
            >
              {ghostInviting ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Sending...</>
              ) : (
                <><Mail className="w-4 h-4 mr-1.5" />Send Invite</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import from Splitwise Dialog */}
      <Dialog open={importOpen} onOpenChange={(open) => { if (!open) { setImportOpen(false); setImportFile(null); setImportStep("upload"); setImportResult(null); } else { setImportOpen(true); }}}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import from Splitwise</DialogTitle>
          </DialogHeader>

          {importResult ? (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 text-primary">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Import complete!</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {importResult.imported} expenses imported, {importResult.skipped} skipped.
              </p>
              {importResult.ghostMembers?.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium mb-1">New members created (ghost):</p>
                  {importResult.ghostMembers.map((g: any) => (
                    <p key={g.id} className="text-muted-foreground">· {g.name}</p>
                  ))}
                </div>
              )}
              <Button className="w-full" onClick={() => { setImportOpen(false); queryClient.invalidateQueries({ queryKey: ["/api/expenses/group", groupId] }); queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId] }); queryClient.invalidateQueries({ queryKey: ["/api/groups", groupId, "members"] }); }}>
                Done
              </Button>
            </div>
          ) : importStep === "upload" ? (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Upload your Splitwise CSV export. Go to Splitwise → Group → Export → Download CSV.
              </p>
              <div
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => document.getElementById("import-csv-input")?.click()}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                {importFile ? (
                  <p className="text-sm font-medium">{importFile.name}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Click to select CSV file</p>
                )}
                <input id="import-csv-input" type="file" accept=".csv" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0]; if (f) setImportFile(f);
                }} />
              </div>
              <Button className="w-full" disabled={!importFile || importLoading} onClick={async () => {
                if (!importFile) return;
                setImportLoading(true);
                try {
                  const text = await importFile.text();
                  const lines = text.split(/\r?\n/).filter(l => l.trim());
                  if (lines.length < 2) { toast({ title: "Error", description: "CSV is empty", variant: "destructive" }); return; }
                  const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());
                  const currIdx = headers.findIndex(h => h.toLowerCase() === "currency");
                  if (currIdx === -1) { toast({ title: "Error", description: "Invalid Splitwise CSV — missing Currency column", variant: "destructive" }); return; }
                  const csvNames = headers.slice(currIdx + 1).map(n => n.trim()).filter(n => n);
                  if (csvNames.length === 0) { toast({ title: "Error", description: "No person columns found in CSV", variant: "destructive" }); return; }

                  // Auto-match: try to match CSV names to group members
                  const mapping: Record<string, string> = {};
                  for (const csvName of csvNames) {
                    const cn = csvName.toLowerCase();
                    const match = members.find(m => m.name.toLowerCase() === cn)
                      || members.find(m => m.name.toLowerCase().includes(cn) || cn.includes(m.name.toLowerCase()))
                      || members.find(m => m.name.toLowerCase().split(" ")[0] === cn.split(" ")[0]);
                    if (match) mapping[csvName] = match.id;
                  }

                  // Parse ALL data rows for accurate count, show first 25 in preview
                  const dateIdx = headers.findIndex(h => h.toLowerCase() === "date");
                  const descIdx = headers.findIndex(h => h.toLowerCase() === "description");
                  const costIdx = headers.findIndex(h => h.toLowerCase() === "cost");
                  const allRows = lines.slice(1).map(line => {
                    const cols = line.split(",").map(c => c.replace(/^"|"$/g, "").trim());
                    return { date: cols[dateIdx] || "", description: cols[descIdx] || "", cost: cols[costIdx] || "0" };
                  }).filter(r => r.description && r.description.length > 0 && !r.description.toLowerCase().includes("total balance"));
                  const preview = allRows;

                  setImportCsvNames(csvNames);
                  setImportMapping(mapping);
                  setImportNewEmails({});
                  setImportPreview(preview);
                  setImportImporterName("");
                  setImportStep("map");
                } catch (err: any) {
                  toast({ title: "Error", description: "Failed to parse CSV", variant: "destructive" });
                } finally {
                  setImportLoading(false);
                }
              }}>
                {importLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {importLoading ? "Parsing..." : "Continue"}
              </Button>
            </div>
          ) : importStep === "map" ? (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Match each person in the CSV to a group member. Select "You" for your own column.
              </p>

              {/* Which CSV column is YOU */}
              <div className="space-y-2">
                <Label>Which column is you?</Label>
                <Select value={importImporterName} onValueChange={v => {
                  setImportImporterName(v);
                  setImportMapping(prev => ({ ...prev, [v]: user?.id || "" }));
                }}>
                  <SelectTrigger><SelectValue placeholder="Select your name in CSV" /></SelectTrigger>
                  <SelectContent>
                    {importCsvNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Map other CSV names to group members */}
              {importCsvNames.filter(n => n !== importImporterName).map(csvName => (
                <div key={csvName} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{csvName}</Label>
                  <Select value={importMapping[csvName] || ""} onValueChange={v => {
                    setImportMapping(prev => ({ ...prev, [csvName]: v }));
                    if (v !== "__new__") setImportNewEmails(prev => { const n = { ...prev }; delete n[csvName]; return n; });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select member..." /></SelectTrigger>
                    <SelectContent>
                      {csvName.toLowerCase().includes("(removed)") && (
                        <SelectItem value="__skip__">Skip — don't add to group</SelectItem>
                      )}
                      {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.email.includes("placeholder") ? "ghost" : m.email})</SelectItem>)}
                      <SelectItem value="__new__">+ New member (enter email)</SelectItem>
                    </SelectContent>
                  </Select>
                  {importMapping[csvName] === "__new__" && (
                    <Input
                      type="email"
                      placeholder="Email address..."
                      value={importNewEmails[csvName] || ""}
                      onChange={e => setImportNewEmails(prev => ({ ...prev, [csvName]: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  )}
                </div>
              ))}

              <div className="flex gap-2">
                <Button variant="outline" className="shrink-0" onClick={() => setImportStep("upload")}>Back</Button>
                <Button className="flex-1 min-w-0" disabled={!importImporterName || importCsvNames.filter(n => n !== importImporterName).some(n => !importMapping[n] || (importMapping[n] === "__new__" && !importNewEmails[n]))} onClick={() => setImportStep("preview")}>
                  Preview ({importPreview.length} expenses)
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                {importPreview.length} expenses will be imported. Duplicates (same date + amount + description) are skipped.
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {importPreview.slice(0, 25).map((r, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-muted/30">
                    <span className="truncate flex-1">{r.description}</span>
                    <span className="text-muted-foreground ml-2">{r.date}</span>
                    <span className="font-medium ml-2">{formatMoney(Number(r.cost), userCurrency)}</span>
                  </div>
                ))}
                {importPreview.length > 25 && (
                  <p className="text-xs text-muted-foreground text-center py-1">... and {importPreview.length - 25} more</p>
                )}
              </div>

              {/* Mapping summary */}
              <div className="text-xs space-y-0.5">
                <p className="font-medium">Member mapping:</p>
                {importCsvNames.map(n => {
                  const mappedId = importMapping[n];
                  const member = members.find(m => m.id === mappedId);
                  const label = n === importImporterName ? "You" : mappedId === "__skip__" ? "Skipped" : mappedId === "__new__" ? `New (${importNewEmails[n]})` : member?.name || "?";
                  return <p key={n} className="text-muted-foreground">{n} → {label}</p>;
                })}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setImportStep("map")}>Back</Button>
                <Button className="flex-1" disabled={importLoading} onClick={async () => {
                  if (!importFile) return;
                  setImportLoading(true);
                  try {
                    const formData = new FormData();
                    formData.append("file", importFile);
                    formData.append("groupId", groupId);
                    formData.append("mapping", JSON.stringify(
                      Object.fromEntries(importCsvNames.map(n => {
                        if (n === importImporterName) return [n, { type: "self" }];
                        if (importMapping[n] === "__skip__") return [n, { type: "skip" }];
                        if (importMapping[n] === "__new__") return [n, { type: "new", email: importNewEmails[n] }];
                        return [n, { type: "member", userId: importMapping[n] }];
                      }))
                    ));
                    const res = await apiFormRequest("POST", `/api/groups/${groupId}/import-mapped`, formData);
                    const data = await res.json();
                    setImportResult(data);
                  } catch (err: any) {
                    let msg = err.message;
                    try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
                    toast({ title: "Import failed", description: msg, variant: "destructive" });
                  } finally {
                    setImportLoading(false);
                  }
                }}>
                  {importLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {importLoading ? "Importing..." : "Import"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
